import React from 'react';
import { Head, Section, SectionHeader } from '@/components';
import { SERVICES } from '@/data/constants';
import { Check } from 'lucide-react';

/**
 * Services page component.
 * Displays service categories and pricing tiers.
 *
 * @returns The services page.
 */
const Services: React.FC = () => {
  return (
    <>
      <Head
        title="Services & Pricing | Kivosystems"
        description="Investment transparency for enterprise-grade engineering. From rapid MVPs to complex legacy migrations, discover our engagement models for high-impact software."
        canonicalUrl="https://www.kivosystems.com/services"
        url="https://www.kivosystems.com/services"
        image="/logo.svg"
        keywords="software development costs, enterprise app pricing, fractional cto rates, system architecture consulting, custom crm development, workflow automation ROI, high performance web apps"
        schemaOverride={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": SERVICES.flatMap(category =>
            category.items.map((item, index) => ({
              "@type": "Service",
              "position": index + 1,
              "name": item.title,
              "description": item.description,
              "provider": {
                "@type": "Organization",
                "name": "Kivosystems",
                "url": "https://www.kivosystems.com"
              },
              "offers": {
                "@type": "Offer",
                "url": "https://www.kivosystems.com/services"
              }
            }))
          )
        }}
      />

      <Section className="bg-obsidian">
        <SectionHeader label="Capabilities" title="Engineering & Capabilities" />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          {SERVICES.map((category) => (
            <div key={category.id} className="border-t border-white/10 pt-8">
              <h3 className="text-2xl font-display mb-8 text-white">{category.category}</h3>
              <div className="space-y-10">
                {category.items.map((item, idx) => (
                  <div key={idx}>
                    <h4 className="text-lg font-bold mb-2 text-gray-100">{item.title}</h4>
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed">{item.description}</p>
                    <ul className="text-xs text-gray-500 space-y-2 uppercase tracking-wide">
                      {item.features.map(f => (
                        <li key={f} className="flex items-center">
                          <Check className="w-3 h-3 mr-2 text-sapphire-500" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section dark className="mt-12 bg-obsidian-50/50">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <span className="text-xs font-bold uppercase tracking-widest text-sapphire-500 mb-4 block">Pricing Guide</span>
          <h2 className="text-3xl md:text-5xl font-display mb-6">Investment Ranges</h2>
          <p className="text-gray-400">
            We believe in being upfront. While every project is unique, these ranges help you understand what to expect for different levels of complexity.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-8 border border-white/5 bg-white/5 backdrop-blur-sm rounded-xl hover:border-white/10 transition-colors flex flex-col h-full">
            <h3 className="text-xl font-display mb-2 text-white">Essential Sites</h3>
            <div className="text-3xl font-bold mb-4 text-sapphire-500">£3k - £8k</div>
            <p className="text-sm text-gray-400 mb-8 min-h-[40px]">Perfect for portfolios, personal brands, or small business brochure websites.</p>

            <div className="space-y-6 mb-8 flex-grow">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Best For</span>
                <p className="text-sm text-gray-300">High-end portfolios, Brochure sites, Landing pages.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Includes</span>
                <p className="text-sm text-gray-300">Custom Design, Content Management (CMS), Basic SEO, Contact Forms, Analytics.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Tech Stack</span>
                <p className="text-sm text-gray-300">Next.js / React (SSG for speed), Vercel Hosting.</p>
              </div>
            </div>

            <div className="pt-6 border-t border-white/5 mt-auto">
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">01</span> 2-4 Weeks</li>
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">02</span> Professional Design</li>
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">03</span> Mobile Responsive</li>
              </ul>
            </div>
          </div>

          <div className="p-8 border border-white/10 bg-white/5 backdrop-blur-sm rounded-xl relative transform md:-translate-y-4 shadow-xl flex flex-col h-full">
            <span className="absolute top-0 right-0 bg-sapphire-500 text-white text-[10px] font-bold uppercase px-3 py-1 tracking-widest rounded-bl-lg rounded-tr-lg">Most Popular</span>
            <h3 className="text-xl font-display mb-2 text-white">Business Growth</h3>
            <div className="text-3xl font-bold mb-4 text-white">£8k - £25k</div>
            <p className="text-sm text-gray-400 mb-8 min-h-[40px]">For robust online stores, booking platforms, or businesses that need more custom features.</p>

            <div className="space-y-6 mb-8 flex-grow">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Best For</span>
                <p className="text-sm text-gray-300">E-commerce, Booking systems, Multi-language sites, Content-heavy platforms.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Includes</span>
                <p className="text-sm text-gray-300">Payment Gateway (Stripe), User Accounts, Inventory Sync, Advanced Filtering, Admin Dashboard.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Tech Stack</span>
                <p className="text-sm text-gray-300">Next.js + Database (PostgreSQL) + API Integrations.</p>
              </div>
            </div>

            <div className="pt-6 border-t border-white/5 mt-auto">
              <ul className="space-y-3 text-sm text-gray-300">
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">01</span> 6-12 Weeks</li>
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">02</span> Full E-Commerce</li>
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">03</span> SEO & Analytics</li>
              </ul>
            </div>
          </div>

          <div className="p-8 border border-white/5 bg-white/5 backdrop-blur-sm rounded-xl hover:border-white/10 transition-colors flex flex-col h-full">
            <h3 className="text-xl font-display mb-2 text-white">Custom Innovation</h3>
            <div className="text-3xl font-bold mb-4 text-gray-400">£25k+</div>
            <p className="text-sm text-gray-400 mb-8 min-h-[40px]">For startups launching a new product or companies automating complex internal workflows.</p>

            <div className="space-y-6 mb-8 flex-grow">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Best For</span>
                <p className="text-sm text-gray-300">SaaS MVPs, Internal Tools, Legacy Migration, AI Integration, Enterprise Platforms.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Includes</span>
                <p className="text-sm text-gray-300">Full Cloud Architecture, API Development, Role-Based Access Control (RBAC), Automated Testing, CI/CD.</p>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-2">Tech Stack</span>
                <p className="text-sm text-gray-300">Full Stack (Python/Node/Go + React), Cloud Infrastructure (AWS/Vercel).</p>
              </div>
            </div>

            <div className="pt-6 border-t border-white/5 mt-auto">
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">01</span> 3+ Months</li>
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">02</span> Custom Software</li>
                <li className="flex items-start"><span className="mr-2 text-gray-600 font-mono">03</span> Dedicated Team</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Services;
